import { CurrentEnvironment, EnvironmentType } from "../../../environment/envTypes"

export const BookingMessageType = {
    BOOKING_COMPLETED: "booking:completed",
    BOOKING_CANCELLED: "booking:cancelled",
    BOOKING_FAILED: "booking:failed",
    DIALOG_CLOSE: "dialog:close",
} as const

export const BookingMessageReason = {
    DIALOG_CLOSE_BY_USER: "manager-dialog-closed-by-user",
} as const

export type BookingMessageTypeValue = (typeof BookingMessageType)[keyof typeof BookingMessageType]

export type BookingCompleted = Readonly<{
    type: typeof BookingMessageType.BOOKING_COMPLETED
    startDateTime: string
    endDateTime: string
    meetingLocation: string
    // orderId: number
}>
export type BookingCancelled = Readonly<{
    type: typeof BookingMessageType.BOOKING_CANCELLED
    reason?: string
}>
export type BookingFailed = Readonly<{
    type: typeof BookingMessageType.BOOKING_FAILED
    reason?: string
}>
export type DialogClose = Readonly<{
    type: typeof BookingMessageType.DIALOG_CLOSE
    reason?: string
}>

export type BookingResultMessage = BookingCompleted | BookingCancelled | BookingFailed | DialogClose
export async function postBookingResultToOutlook(
    msg: BookingResultMessage,
    parentOrigin: string,
    currentEnv: CurrentEnvironment,
) {
    const json = JSON.stringify(msg)

    if (currentEnv.type !== EnvironmentType.Local) {
        await Office.onReady()
        Office.context.ui.messageParent(json, { targetOrigin: parentOrigin })
    } else {
        const officeReady = Promise.resolve()
            .then(() => Office.onReady())
            .then(() => true)
            .catch(() => false)

        // In local/tunnel runs, Office.onReady() can hang; don't block sending forever.
        const isReady = await Promise.race([
            officeReady,
            new Promise((resolve) => setTimeout(() => resolve(false), 1500)),
        ])
        console.log("ttt postBookingResultToOutlook officeReady", isReady)

        // First try strict targetOrigin, then fall back to "*" if Office rejects or drops it in dev/tunnel.
        try {
            if (!Office.context?.ui?.messageParent) throw new Error("Office.context.ui.messageParent is not available")
            Office.context.ui.messageParent(json, { targetOrigin: parentOrigin })
            console.log("ttt postBookingResultToOutlook sent strict")
            return
        } catch (e) {
            console.warn("ttt postBookingResultToOutlook strict failed (will fall back)", e)
        }

        try {
            if (Office.context?.ui?.messageParent) {
                Office.context.ui.messageParent(json)
                console.log("ttt postBookingResultToOutlook sent fallback messageParent")
                return
            }

            // If this page isn't running as an Office dialog (local/tunnel can be flaky),
            // fall back to posting to the parent window (dialog-redirect wrapper) which relays to Outlook.
            const dialogOrigin =
                new URLSearchParams(window.location.search).get("dialogOrigin") || window.location.origin
            if (window.parent && window.parent !== window) {
                window.parent.postMessage(json, dialogOrigin)
                console.log("ttt postBookingResultToOutlook sent fallback postMessage", { dialogOrigin })
                return
            }

            if (window.opener && typeof window.opener.postMessage === "function") {
                window.opener.postMessage(json, dialogOrigin)
                console.log("ttt postBookingResultToOutlook sent fallback postMessage opener", { dialogOrigin })
                return
            }

            throw new Error("No Office.messageParent and no parent/opener to postMessage")
        } catch (e) {
            console.error("ttt postBookingResultToOutlook fallback failed", e)
        }
    }
}
